import { useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"

import { useEditOrderNewMutation, useLazyGetOrdersNewQuery } from "store/api/ordersApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes } from "types"
import { ReactComponent as IconNewOrder } from "assets/svg/modal-icon-new-order.svg"

import getErrorMessage from "utils/getErrorMessage"
import OrdersModalContent from "modals/NewOrderModal/OrdersModalContent"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

interface Props {
  onCancel: () => void
}

export const EditOrderModal = ({ onCancel }: Props) => {
  const [editOrderNew, { isLoading }] = useEditOrderNewMutation()
  const [triggerGetOrders] = useLazyGetOrdersNewQuery()
  const currentOrder = useAppSelector(state => state.tempData.currentOrderNew)

  const [selectedDevices, setSelectedDevices] = useState<Record<ApiTypes.Model.OrderProductType, boolean>>({
    thermostat: false,
    cwa: false,
    ...(currentOrder?.items.reduce(
      (acc, item) => {
        if (item.product_type === "thermostat" || item.product_type === "cwa") {
          acc[item.product_type] = true
        }
        return acc
      },
      {} as Record<ApiTypes.Model.OrderProductType, boolean>,
    ) || {}),
  })
  const [quantities, setQuantities] = useState<Record<ApiTypes.Model.OrderProductType, number>>({
    thermostat: 20,
    cwa: 1,
    ...(currentOrder?.items.reduce(
      (acc, item) => {
        if (item.product_type === "thermostat" || item.product_type === "cwa") {
          acc[item.product_type] = (acc[item.product_type] || 0) + item.quantity
        }
        return acc
      },
      {} as Record<ApiTypes.Model.OrderProductType, number>,
    ) || {}),
  })

  const handleDeviceToggle = (deviceType: ApiTypes.Model.OrderProductType) => {
    setSelectedDevices(prev => ({
      ...prev,
      [deviceType]: !prev[deviceType],
    }))
  }

  const handleQuantityChange = (deviceType: ApiTypes.Model.OrderProductType, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [deviceType]: quantity,
    }))
  }

  const hasSelectedDevices = Object.values(selectedDevices).some(isSelected => isSelected)

  const onSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()

    const selectedDeviceTypes = Object.entries(selectedDevices)
      .filter(([, isSelected]) => isSelected)
      .map(([deviceType]) => deviceType as ApiTypes.Model.OrderProductType)

    try {
      const res = await editOrderNew({
        data: {
          items: selectedDeviceTypes.map(deviceType => ({
            quantity: quantities[deviceType],
            product_type: deviceType,
          })),
        },
        id: currentOrder?.id || 0,
      })

      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("Order have been successfully updated!")
        triggerGetOrders({})
        onCancel()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="570px"
          onConfirm={onSubmit}
          onCancel={onCancel}
          isFetching={isLoading}
          isDisabled={!hasSelectedDevices}
        >
          <ModalHeader title="Edit order" subtitle="Please select device types and input quantities you want to order.">
            <IconNewOrder />
          </ModalHeader>
          <OrdersModalContent
            selectedDevices={selectedDevices}
            quantities={quantities}
            onDeviceToggle={handleDeviceToggle}
            onQuantityChange={handleQuantityChange}
          />
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
